import React, { useState, useEffect } from 'react';
import { getAllUsers, logoutUser } from '../firebase';

const BRAND = '#0f6e56';

export default function AdminDashboard({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('all');
  const [country, setCountry] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [copied, setCopied] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    const load = async () => {
      const data = await getAllUsers();
      setUsers(data);
      setLoading(false);
    };
    load();
  }, []);

  const handleLogout = async () => { await logoutUser(); window.location.reload(); };

  const countries = [...new Set(users.map(u => u.country).filter(Boolean))];

  const filtered = users
    .filter(u => {
      const matchSearch =
        (u.businessName || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.phone || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.ownerName || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.city || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.country || '').toLowerCase().includes(search.toLowerCase());
      const matchPlatform = platform === 'all' || u.platform === platform;
      const matchCountry = country === 'all' || u.country === country;
      return matchSearch && matchPlatform && matchCountry;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return (b.registeredAt?.seconds || 0) - (a.registeredAt?.seconds || 0);
      if (sortBy === 'oldest') return (a.registeredAt?.seconds || 0) - (b.registeredAt?.seconds || 0);
      if (sortBy === 'name') return (a.businessName || '').localeCompare(b.businessName || '');
      if (sortBy === 'country') return (a.country || '').localeCompare(b.country || '');
      return 0;
    });

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const exportCSV = () => {
    const headers = ['Business Name','Owner Name','Email','Phone','City','Country','Platform','Currency','Registered'];
    const rows = filtered.map(u => [
      u.businessName || '', u.ownerName || '', u.email || '', u.phone || '',
      u.city || '', u.country || '', u.platform || 'mobile', u.currency || '$',
      u.registeredAt?.toDate ? u.registeredAt.toDate().toLocaleDateString() : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + v + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'FinlyBooks_Users_' + new Date().toISOString().slice(0,10) + '.csv'; a.click();
  };

  const exportEmails = () => copyToClipboard(filtered.map(u => u.email).filter(Boolean).join(', '), 'emails');
  const exportPhones = () => copyToClipboard(filtered.map(u => u.phone).filter(Boolean).join(', '), 'phones');
  const exportWhatsApp = () => {
    const list = filtered.map((u,i) => (i+1) + '. ' + (u.businessName||'N/A') + ' | ' + (u.phone||'No phone') + ' | ' + (u.email||'No email') + ' | ' + (u.country||'N/A')).join('\n');
    copyToClipboard('FinlyBooks Users (' + filtered.length + ' of ' + users.length + '):\n\n' + list + '\n\nwww.finlybooks.com', 'whatsapp');
  };

  const webUsers = users.filter(u => u.platform === 'web').length;
  const mobileUsers = users.filter(u => u.platform === 'mobile').length;

  return (
    <div style={{ minHeight:'100vh', background:'#f7f8fa', fontFamily:'-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif' }}>

      {/* Header */}
      <div style={{ background:BRAND, padding:'0 32px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <span style={{ color:'#fff', fontSize:20, fontWeight:700 }}>FinlyBooks</span>
          <span style={{ color:'rgba(255,255,255,0.5)', fontSize:13 }}>Admin Dashboard</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <span style={{ color:'rgba(255,255,255,0.75)', fontSize:13 }}>{user.email}</span>
          <button onClick={handleLogout} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', padding:'7px 16px', borderRadius:8, cursor:'pointer', fontSize:13 }}>Logout</button>
        </div>
      </div>

      <div style={{ padding:28 }}>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:14, marginBottom:24 }}>
          {[
            { label:'Total Users', value:users.length, color:BRAND, bg:'#e1f5ee', icon:'👥' },
            { label:'Web Users', value:webUsers, color:'#185fa5', bg:'#e6f1fb', icon:'💻' },
            { label:'Mobile Users', value:mobileUsers, color:'#854f0b', bg:'#faeeda', icon:'📱' },
            { label:'Countries', value:countries.length, color:'#534ab7', bg:'#eeedfb', icon:'🌍' },
            { label:'With Phone', value:users.filter(u=>u.phone).length, color:'#25d366', bg:'#e8faf0', icon:'📞' },
            { label:'Filtered', value:filtered.length, color:'#a32d2d', bg:'#fcebeb', icon:'🔍' },
          ].map(s => (
            <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:'16px 20px' }}>
              <div style={{ fontSize:18, marginBottom:4 }}>{s.icon}</div>
              <div style={{ fontSize:26, fontWeight:700, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:11, color:'#555', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:0, marginBottom:20, background:'#fff', borderRadius:12, border:'1px solid #e8e8e8', overflow:'hidden', width:'fit-content' }}>
          {[
            { id:'users', label:'👥 All Users' },
            { id:'export', label:'📤 Export & Contact' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding:'12px 24px', border:'none', cursor:'pointer', fontSize:14, fontWeight: activeTab===t.id ? 600 : 400,
              background: activeTab===t.id ? BRAND : '#fff',
              color: activeTab===t.id ? '#fff' : '#666',
              borderRight: '1px solid #e8e8e8'
            }}>{t.label}</button>
          ))}
        </div>

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div style={{ background:'#fff', borderRadius:14, border:'1px solid #e8e8e8', overflow:'hidden' }}>
            {/* Filters bar */}
            <div style={{ padding:'16px 24px', borderBottom:'1px solid #e8e8e8', display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search name, email, phone, city, country..."
                style={{ padding:'8px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:13, outline:'none', flex:1, minWidth:220 }} />

              <select value={platform} onChange={e=>setPlatform(e.target.value)} style={selStyle}>
                <option value="all">All platforms</option>
                <option value="web">Web only</option>
                <option value="mobile">Mobile only</option>
              </select>

              <select value={country} onChange={e=>setCountry(e.target.value)} style={selStyle}>
                <option value="all">All countries</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={selStyle}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Name A-Z</option>
                <option value="country">Country A-Z</option>
              </select>

              <button onClick={() => { setSearch(''); setPlatform('all'); setCountry('all'); setSortBy('newest'); }}
                style={{ padding:'8px 14px', background:'#f7f8fa', border:'1px solid #ddd', borderRadius:8, fontSize:13, cursor:'pointer', color:'#666' }}>
                Clear filters
              </button>

              <div style={{ fontSize:12, color:'#aaa' }}>Showing {filtered.length} of {users.length}</div>
            </div>

            {loading ? (
              <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>Loading users...</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>👥</div>
                <div style={{ fontSize:16 }}>No users match your filters</div>
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#f7f8fa' }}>
                      {['#','Business','Owner','Email','Phone','Location','Platform','Registered','Actions'].map(h => (
                        <th key={h} style={{ padding:'11px 16px', textAlign:'left', fontSize:11, color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:0.3, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u, i) => (
                      <tr key={u.id} style={{ borderTop:'1px solid #f0f0f0', background: i%2===0 ? '#fff' : '#fafafa' }}
                        onMouseEnter={e=>e.currentTarget.style.background='#f0faf6'}
                        onMouseLeave={e=>e.currentTarget.style.background= i%2===0 ? '#fff' : '#fafafa'}>
                        <td style={td}><span style={{ color:'#bbb', fontSize:12 }}>{i+1}</span></td>
                        <td style={td}><span style={{ fontWeight:700, color:'#222' }}>{u.businessName || '—'}</span></td>
                        <td style={td}>{u.ownerName || '—'}</td>
                        <td style={td}>
                          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <a href={'mailto:'+u.email} style={{ color:BRAND, textDecoration:'none', fontSize:13 }}>{u.email || '—'}</a>
                            {u.email && <button onClick={()=>copyToClipboard(u.email,u.id+'e')} title="Copy" style={iconBtn}>{copied===u.id+'e'?'✅':'📋'}</button>}
                          </div>
                        </td>
                        <td style={td}>
                          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                            {u.phone
                              ? <a href={'https://wa.me/'+u.phone.replace(/[^0-9]/g,'')} target="_blank" rel="noreferrer" style={{ color:'#25d366', textDecoration:'none', fontSize:13 }}>{u.phone}</a>
                              : <span style={{ color:'#ccc' }}>—</span>}
                            {u.phone && <button onClick={()=>copyToClipboard(u.phone,u.id+'p')} title="Copy" style={iconBtn}>{copied===u.id+'p'?'✅':'📋'}</button>}
                          </div>
                        </td>
                        <td style={td}>{[u.city,u.country].filter(Boolean).join(', ')||'—'}</td>
                        <td style={td}>
                          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600,
                            background: u.platform==='web'?'#e6f1fb':'#e1f5ee',
                            color: u.platform==='web'?'#185fa5':BRAND }}>
                            {u.platform==='web'?'💻 Web':'📱 Mobile'}
                          </span>
                        </td>
                        <td style={{ ...td, color:'#888', fontSize:12 }}>{u.registeredAt?.toDate ? u.registeredAt.toDate().toLocaleDateString() : '—'}</td>
                        <td style={td}>
                          <div style={{ display:'flex', gap:5 }}>
                            {u.email && <a href={'mailto:'+u.email} title="Email" style={actionBtn('#e1f5ee')}>📧</a>}
                            {u.phone && <a href={'https://wa.me/'+u.phone.replace(/[^0-9]/g,'')} target="_blank" rel="noreferrer" title="WhatsApp" style={actionBtn('#e8faf0')}>💬</a>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* EXPORT TAB */}
        {activeTab === 'export' && (
          <div>
            {/* Filter dropdown for export */}
            <div style={{ background:'#fff', borderRadius:14, padding:20, border:'1px solid #e8e8e8', marginBottom:20 }}>
              <div style={{ fontSize:15, fontWeight:600, marginBottom:14 }}>🔽 Filter before exporting</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <select value={platform} onChange={e=>setPlatform(e.target.value)} style={{ ...selStyle, flex:1 }}>
                  <option value="all">All platforms ({users.length})</option>
                  <option value="web">Web users only ({webUsers})</option>
                  <option value="mobile">Mobile users only ({mobileUsers})</option>
                </select>
                <select value={country} onChange={e=>setCountry(e.target.value)} style={{ ...selStyle, flex:1 }}>
                  <option value="all">All countries</option>
                  {countries.map(c => <option key={c} value={c}>{c} ({users.filter(u=>u.country===c).length})</option>)}
                </select>
                <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{ ...selStyle, flex:1 }}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name">Name A-Z</option>
                  <option value="country">Country A-Z</option>
                </select>
              </div>
              <div style={{ marginTop:10, fontSize:13, color:'#888' }}>
                Ready to export: <strong style={{ color:BRAND }}>{filtered.length} users</strong> matching your filters
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:16 }}>
              <ExportCard
                icon="📊" title="Export to Excel / CSV"
                desc={'Download ' + filtered.length + ' users as a spreadsheet. Opens in Excel, Google Sheets or Numbers.'}
                btnLabel="Download CSV" btnColor={BRAND} onClick={exportCSV}
              />
              <ExportCard
                icon="📧" title="Copy all emails"
                desc={'Copy ' + filtered.filter(u=>u.email).length + ' email addresses to clipboard. Paste into Gmail, Mailchimp or any email tool.'}
                btnLabel={copied==='emails' ? '✅ Copied!' : 'Copy emails'} btnColor="#185fa5" onClick={exportEmails}
              />
              <ExportCard
                icon="📞" title="Copy all phone numbers"
                desc={'Copy ' + filtered.filter(u=>u.phone).length + ' phone numbers to clipboard. Use for WhatsApp broadcast or SMS.'}
                btnLabel={copied==='phones' ? '✅ Copied!' : 'Copy phones'} btnColor="#854f0b" onClick={exportPhones}
              />
              <ExportCard
                icon="💬" title="Copy WhatsApp list"
                desc={'Copy a formatted list of all ' + filtered.length + ' users with name, phone, email and country. Perfect for WhatsApp broadcast.'}
                btnLabel={copied==='whatsapp' ? '✅ Copied!' : 'Copy list'} btnColor="#25d366" onClick={exportWhatsApp}
              />
              <ExportCard
                icon="📨" title="Email all users"
                desc={'Opens your email client with all ' + filtered.filter(u=>u.email).length + ' email addresses pre-filled. Send a bulk announcement.'}
                btnLabel="Open email" btnColor="#a32d2d"
                href={'mailto:' + filtered.map(u=>u.email).filter(Boolean).join(',')}
              />
              <ExportCard
                icon="📋" title="Preview export"
                desc={'See exactly what will be exported — ' + filtered.length + ' users with all their details.'}
                btnLabel="View users tab" btnColor="#534ab7" onClick={() => setActiveTab('users')}
              />
            </div>

            <div style={{ background:'#fff', borderRadius:14, padding:20, border:'1px solid #e8e8e8', marginTop:20 }}>
              <div style={{ fontSize:14, fontWeight:600, marginBottom:14 }}>🌍 Quick filter by country</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                <button onClick={()=>setCountry('all')} style={{ padding:'7px 14px', borderRadius:8, fontSize:13, cursor:'pointer', border:'1px solid '+(country==='all'?BRAND:'#ddd'), background: country==='all'?BRAND:'#fff', color: country==='all'?'#fff':BRAND, fontWeight:600 }}>
                  All ({users.length})
                </button>
                {countries.map(c => (
                  <button key={c} onClick={()=>setCountry(c)} style={{ padding:'7px 14px', borderRadius:8, fontSize:13, cursor:'pointer', border:'1px solid '+(country===c?BRAND:'#ddd'), background: country===c?BRAND:'#fff', color: country===c?'#fff':BRAND, fontWeight:600 }}>
                    {c} ({users.filter(u=>u.country===c).length})
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ textAlign:'center', marginTop:24, fontSize:12, color:'#aaa' }}>
          FinlyBooks Admin · © 2025 FinlyBooks · info@finlybooks.com · www.finlybooks.com
        </div>
      </div>
    </div>
  );
}

function ExportCard({ icon, title, desc, btnLabel, btnColor, onClick, href }) {
  return (
    <div style={{ background:'#fff', borderRadius:14, padding:24, border:'1px solid #e8e8e8', display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ fontSize:28 }}>{icon}</div>
      <div style={{ fontSize:15, fontWeight:600, color:'#222' }}>{title}</div>
      <div style={{ fontSize:13, color:'#888', lineHeight:1.6, flex:1 }}>{desc}</div>
      {href ? (
        <a href={href} style={{ padding:'10px 16px', background:btnColor, color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer', textDecoration:'none', textAlign:'center' }}>{btnLabel}</a>
      ) : (
        <button onClick={onClick} style={{ padding:'10px 16px', background:btnColor, color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer' }}>{btnLabel}</button>
      )}
    </div>
  );
}

const selStyle = { padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:13, outline:'none', background:'#fff', cursor:'pointer' };
const td = { padding:'12px 16px', fontSize:13, color:'#444', whiteSpace:'nowrap' };
const iconBtn = { background:'none', border:'none', cursor:'pointer', fontSize:13, padding:'2px 4px' };
const actionBtn = bg => ({ padding:'5px 8px', background:bg, borderRadius:6, fontSize:14, textDecoration:'none', border:'none', cursor:'pointer' });
